<template>
  <div class="home-container">
    <!-- 加载状态 -->

    <div v-if="loading" class="home-loading-container">
      <i class="el-icon-loading"></i>

      <p>正在加载...</p>
    </div>

    <!-- 主要内容 -->

    <div v-else>
      <!-- 主轮播图区域 (包含分类导航) -->

      <section class="home-banner-section">
        <div class="home-banner-wrapper">
          <!-- 左侧分类导航 -->
          <div class="home-category-nav">
            <div class="home-category-nav-header">
              <h3>服务分类</h3>
            </div>
            <div class="home-category-nav-list">
              <div
                v-for="category in validParentCategories"
                :key="category.id"
                :ref="`category-item-${category.id}`"
                class="home-category-nav-item"
                :class="{ active: selectedParentId === category.id }"
                @click="handleCategoryNavClick(category, $event)"
                @mouseenter="handleCategoryHover(category, $event)"
                @mouseleave="handleCategoryLeave"
              >
                <div class="home-category-nav-icon">
                  <img
                    :src="getImageUrl(category.imageUrl)"
                    :alt="category.title"
                  />
                </div>
                <span class="home-category-nav-name">{{ category.title }}</span>
                <i class="el-icon-arrow-right"></i>
              </div>
            </div>
          </div>

          <!-- 右侧轮播图 -->
          <div class="home-banner-content">
            <el-carousel
              v-if="banners.length > 0"
              :interval="4000"
              height="600px"
            >
              <el-carousel-item v-for="banner in banners" :key="banner.id">
                <img
                  :src="banner.image"
                  :alt="banner.title || '轮播图'"
                  @error="handleImageError"
                  @load="handleImageLoad"
                />
              </el-carousel-item>
            </el-carousel>

            <!-- 轮播图加载失败时的占位符 -->
            <div v-else class="home-banner-placeholder">
              <div class="placeholder-content">
                <i class="el-icon-picture-outline"></i>
                <p>轮播图加载中...</p>
              </div>
            </div>
          </div>

          <!-- 二级分类悬浮层 - 移动到整个wrapper层级 -->
          <div
            v-if="showSubCategoryFloat && selectedParentCategory"
            class="home-sub-category-float"
            :style="{
              top: `${currentHoverItemTop}px`,
              left: `${currentHoverItemLeft}px`,
            }"
            @mouseenter="keepFloatVisible"
            @mouseleave="hideSubCategoryFloat"
          >
            <div class="home-sub-category-float-header">
              <h4>{{ selectedParentCategory.title }}</h4>
              <span class="home-sub-category-count"
                >{{
                  getSubCategoryCount(selectedParentCategory.id)
                }}
                个服务子类</span
              >
            </div>
            <div class="home-sub-category-float-grid">
              <div
                v-for="subCategory in computedCurrentSubCategories"
                :key="subCategory.id"
                class="home-sub-category-float-item"
                @click="handleSubCategoryClick(subCategory)"
              >
                {{ subCategory.name }}
              </div>
            </div>
            <div
              v-if="!computedCurrentSubCategories.length"
              class="home-sub-category-empty"
            >
              暂无相关子分类
            </div>
          </div>
        </div>
      </section>

      <!-- 商家推荐区域 -->

      <section class="home-merchant-section">
        <div class="home-section-container">
          <div class="home-section-header">
            <h2>推荐商家</h2>

            <el-button
              type="text"
              @click="navigateToMerchants"
              class="home-more-btn"
            >
              查看更多
              <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
          <div v-if="recommendMerchants.length" class="home-merchant-waterfall">
            <div
              v-for="merchant in recommendMerchants"
              :key="merchant.id"
              class="home-merchant-card"
              @click="handleMerchantClick(merchant)"
            >
              <div class="home-merchant-image">
                <img
                  :src="merchant.image"
                  :alt="merchant.name"
                  @load="handleMerchantImageLoad"
                />
              </div>
              <div class="home-merchant-info">
                <h3 class="home-merchant-name">{{ merchant.name }}</h3>
                <div class="home-merchant-meta">
                  <span class="home-merchant-service">{{
                    merchant.service
                  }}</span>
                  <span class="home-merchant-area">{{ merchant.area }}</span>
                </div>
                <div class="home-merchant-rating">
                  <el-rate
                    v-model="merchant.rating"
                    disabled
                    show-score
                    text-color="#ff9900"
                    score-template="{value}"
                  />
                </div>
                <p class="home-merchant-remark">{{ merchant.remark }}</p>
              </div>
            </div>
          </div>
          <div v-else class="home-empty-block">
            <i class="el-icon-warning-outline"></i>
            <p>暂无推荐商家</p>
            <small>稍后再来看看，或尝试切换其它城市</small>
            <el-button type="text" @click="navigateToMerchants"
              >去商家列表</el-button
            >
          </div>
        </div>
      </section>

      <!-- 热门服务区域 -->

      <section class="home-service-section">
        <div class="home-section-container">
          <div class="home-section-header">
            <h2>热门服务</h2>

            <el-button
              type="text"
              @click="navigateToServices"
              class="home-more-btn"
            >
              查看更多
              <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>

          <div v-if="hotServices.length" class="home-service-waterfall">
            <div
              v-for="service in hotServices"
              :key="service.id"
              class="home-service-card"
              @click="handleServiceClick(service)"
            >
              <div class="home-service-image">
                <img
                  :src="service.image"
                  :alt="service.title"
                  @load="handleServiceImageLoad"
                />
                <div class="home-service-badge" v-if="service.salesNumber > 0">
                  已售{{ service.salesNumber }}
                </div>
              </div>
              <div class="home-service-info">
                <h3 class="home-service-title">{{ service.title }}</h3>
                <p class="home-service-description">
                  {{ service.description }}
                </p>
                <div class="home-service-meta">
                  <span class="home-service-merchant">{{
                    service.merchantName
                  }}</span>
                  <span class="home-service-area">{{ service.address }}</span>
                </div>
                <div class="home-service-footer">
                  <div class="home-service-price">¥{{ service.price }}</div>
                  <div class="home-service-rating" v-if="service.evaluate">
                    <i class="el-icon-star-on"></i>
                    <span>{{ service.evaluate }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="home-empty-block">
            <i class="el-icon-info"></i>
            <p>暂无热门服务</p>
            <small>您可以前往服务列表查看更多内容</small>
            <el-button type="text" @click="navigateToServices"
              >去服务列表</el-button
            >
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { noPermissionRequired } from "@/api/index";
import { getImageUrl } from "@/utils/imageUtils";
import {
  API_PARAMS,
  DELAY_TIMES,
  DEFAULT_VALUES,
  FILTER_RULES,
  IMAGE_CONFIG,
  ERROR_MESSAGES,
} from "./config.js";

export default {
  name: "Home",
  data() {
    return {
      loading: true,
      banners: [],
      parentCategories: [],
      subCategories: [],
      selectedParentId: null,
      selectedParentCategory: null,
      currentSubCategories: [],
      recommendMerchants: [],
      hotServices: [],
      platformInfo: {},
      // 新增：控制二级分类悬浮层显示
      showSubCategoryFloat: false,
      floatHideTimer: null,
      // 新增：当前悬停项的位置信息
      currentHoverItemTop: 0,
      currentHoverItemLeft: 240, // 悬浮层的左侧位置（分类导航宽度）
    };
  },

  computed: {
    // 计算属性：获取有效的父级分类
    validParentCategories() {
      return this.parentCategories.filter((item) => item.delFlag === "0");
    },

    // 计算属性：获取有效的子级分类
    validSubCategories() {
      return this.subCategories.filter(
        (item) => item.delFlag === "0" && item.serviceTypeMasterId && item.name
      );
    },

    // 计算属性：当前子分类列表
    computedCurrentSubCategories() {
      if (!this.selectedParentId) return [];
      return this.validSubCategories.filter(
        (sub) => sub.serviceTypeMasterId === this.selectedParentId
      );
    },
  },

  watch: {
    // 监听选中的父级分类变化
    selectedParentId: {
      handler() {
        this.currentSubCategories = this.computedCurrentSubCategories;
      },
      immediate: true,
    },
  },

  async created() {
    await this.initializeHomePage();
    // 监听地域切换事件（来自 Header 组件）
    if (this.$bus) {
      this.$bus.on("region-changed", this.handleRegionChange);
    }
  },

  beforeDestroy() {
    // 清理定时器
    this.clearFloatHideTimer();
    // 移除事件监听
    if (this.$bus) {
      this.$bus.off("region-changed", this.handleRegionChange);
    }
  },

  methods: {
    // 工具方法：获取图片URL
    getImageUrl,

    /**
     * 工具方法：随机排序数组
     * @param {Array} array - 需要随机排序的数组
     * @returns {Array} 随机排序后的新数组
     */
    shuffleArray(array) {
      if (!Array.isArray(array) || array.length <= 1) {
        return array;
      }

      // 创建数组副本，避免修改原数组
      const shuffled = [...array];

      // Fisher-Yates 洗牌算法
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      return shuffled;
    },

    /**
     * 从API响应中提取数据
     * @param {Object} response - API响应对象
     * @returns {Array} 提取的数据数组
     */
    extractDataFromResponse(response) {
      if (!response?.ok || response.code !== 0 || !response.data) {
        return [];
      }
      return Array.isArray(response.data)
        ? response.data
        : response.data.records || [];
    },

    /**
     * 从API响应中提取单个对象
     * @param {Object} response - API响应对象
     * @returns {Object|null} 提取的数据对象
     */
    extractSingleDataFromResponse(response) {
      if (!response?.ok || response.code !== 0 || !response.data) {
        return null;
      }
      return Array.isArray(response.data)
        ? response.data[0] || null
        : response.data.records?.[0] || response.data;
    },

    /**
     * 初始化首页
     */
    async initializeHomePage() {
      try {
        this.loading = true;

        // 添加最小加载时间，避免闪烁
        await Promise.all([
          this.loadHomeData(),
          this.delay(DELAY_TIMES.MIN_LOADING), // 最小加载时间
        ]);
      } catch (error) {
        this.handleError(ERROR_MESSAGES.INIT_FAILED, error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * 并行加载首页数据
     */
    async loadHomeData() {
      try {
        // 先加载平台信息（影响后续地域筛选开关）
        await this.loadPlatformInfo();
        // 再并行加载其他模块数据
        await Promise.all([
          this.loadBanners(),
          this.loadCategoryData(),
          this.loadRecommendMerchants(),
          this.loadHotServices(),
        ]);
      } catch (error) {
        throw new Error(`数据加载失败: ${error.message}`);
      }
    },

    /**
     * 统一错误处理
     */
    handleError(message, error) {
      console.error(message, error);
      this.$msg.error(`${message}，请刷新重试`);
    },

    /**
     * 延迟工具函数
     */
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    /**
     * 加载分类数据
     */
    async loadCategoryData() {
      try {
        // 加载父级分类数据
        const parentResponse = await noPermissionRequired.getServiceTypeMaster(
          API_PARAMS.PARENT_CATEGORIES
        );
        const parentData = this.extractDataFromResponse(parentResponse);
        this.parentCategories = this.filterValidData(parentData);

        // 加载子级分类数据
        const subResponse = await noPermissionRequired.getServiceTypeDetail(
          API_PARAMS.SUB_CATEGORIES
        );
        const subData = this.extractDataFromResponse(subResponse);
        this.subCategories = this.filterValidSubCategories(subData);

        // 自动选择第一个父级分类
        this.selectFirstParentCategory();
      } catch (error) {
        console.error("加载分类数据失败:", error);
        this.$msg.warning(ERROR_MESSAGES.CATEGORY_FAILED);
      }
    },

    /**
     * 过滤有效数据（通用方法）
     */
    filterValidData(data) {
      return data.filter(
        (item) => (item.delFlag || item.deleted || "0") === "0"
      );
    },

    /**
     * 过滤有效的子分类数据
     */
    filterValidSubCategories(data) {
      return this.filterValidData(data).filter(
        (item) => item.serviceTypeMasterId && item.name
      );
    },

    /**
     * 选择第一个父级分类
     */
    selectFirstParentCategory() {
      if (this.validParentCategories.length > 0) {
        this.handleParentCategoryClick(this.validParentCategories[0]);
      }
    },

    /**
     * 获取指定父级分类下的子分类数量
     */
    getSubCategoryCount(parentId) {
      return this.validSubCategories.filter(
        (sub) => sub.serviceTypeMasterId === parentId
      ).length;
    },

    /**
     * 处理父级分类点击
     */
    handleParentCategoryClick(category) {
      if (!category?.id) return;

      this.selectedParentId = category.id;
      this.selectedParentCategory = category;
    },

    /**
     * 处理分类导航点击 (新方法)
     */
    handleCategoryNavClick(category, event) {
      if (!category?.id) return;

      this.selectedParentId = category.id;
      this.selectedParentCategory = category;
      this.clearFloatHideTimer();

      // 计算位置（复用悬停逻辑）
      this.handleCategoryHover(category, event);
    },

    /**
     * 处理分类悬停 (新方法)
     */
    handleCategoryHover(category, event) {
      if (!category?.id) return;

      this.clearFloatHideTimer();
      this.selectedParentId = category.id;
      this.selectedParentCategory = category;

      // 计算当前悬停项的位置 - 改为基于整个轮播图区域
      const target = event.currentTarget;
      const bannerWrapper = target.closest(".home-banner-wrapper");
      if (target && bannerWrapper) {
        const targetRect = target.getBoundingClientRect();
        const wrapperRect = bannerWrapper.getBoundingClientRect();

        // 基础位置计算 - 相对于wrapper容器
        let top = targetRect.top - wrapperRect.top;
        let left = 240; // 分类导航宽度

        // 边界检测和调整
        const floatWidth = 360; // 悬浮层宽度
        const floatMaxHeight = 320; // 悬浮层最大高度
        const wrapperWidth = bannerWrapper.offsetWidth; // 使用offsetWidth获取实际宽度
        const wrapperHeight = bannerWrapper.offsetHeight; // 使用offsetHeight获取实际高度
        const margin = 0; // 边距

        // 计算悬浮层实际需要的高度（基于子分类数量）
        const subCategoryCount = this.getSubCategoryCount(category.id) || 0;
        const headerHeight = 48; // 头部高度
        const itemHeight = 40; // 每个子分类项高度（包含间距）
        const padding = 24; // 内边距
        const gridRows = Math.ceil(subCategoryCount / 2); // 2列布局，计算行数
        const actualFloatHeight = Math.min(
          headerHeight + gridRows * itemHeight + padding,
          floatMaxHeight
        );

        // 右边界检测 - 智能调整左侧位置
        const availableRightSpace = wrapperWidth - left;
        if (floatWidth > availableRightSpace) {
          // 空间不足，向左调整
          const newLeft = wrapperWidth - floatWidth - margin;
          left = Math.max(margin, newLeft); // 确保不超出左边界
        }

        // 下边界检测 - 智能调整垂直位置
        const availableBottomSpace = wrapperHeight - top;
        if (actualFloatHeight > availableBottomSpace) {
          // 向上调整位置
          const newTop = wrapperHeight - actualFloatHeight - margin;
          top = Math.max(margin, newTop); // 确保不超出顶部

          // 如果向上调整后仍然放不下，则使用最大可用空间
          if (
            top === margin &&
            actualFloatHeight > wrapperHeight - 2 * margin
          ) {
            // 此时悬浮层会有滚动条显示
            console.log("悬浮层内容过多，将显示滚动条");
          }
        }

        // 最终边界确保
        top = Math.max(margin, Math.min(top, wrapperHeight - margin));
        left = Math.max(
          margin,
          Math.min(left, wrapperWidth - floatWidth - margin)
        );

        this.currentHoverItemTop = top;
        this.currentHoverItemLeft = left;
      }

      this.showSubCategoryFloat = true;
    },
    /**
     * 处理分类离开 (新方法)
     */ handleCategoryLeave() {
      this.floatHideTimer = setTimeout(() => {
        this.showSubCategoryFloat = false;
      }, 200);
    },

    /**
     * 保持悬浮层可见 (新方法)
     */
    keepFloatVisible() {
      this.clearFloatHideTimer();
    },

    /**
     * 隐藏二级分类悬浮层 (新方法)
     */
    hideSubCategoryFloat() {
      this.floatHideTimer = setTimeout(() => {
        this.showSubCategoryFloat = false;
      }, 200);
    },

    /**
     * 清除隐藏定时器 (新方法)
     */
    clearFloatHideTimer() {
      if (this.floatHideTimer) {
        clearTimeout(this.floatHideTimer);
        this.floatHideTimer = null;
      }
    },

    /**
     * 处理子分类点击
     */
    handleSubCategoryClick(subCategory) {
      if (!subCategory?.id || !this.selectedParentCategory) {
        this.$msg.warning(ERROR_MESSAGES.CATEGORY_INFO_INCOMPLETE);
        return;
      }

      console.log(
        "跳转到服务列表，子分类ID:",
        subCategory.id,
        "子分类名称:",
        subCategory.name
      );

      this.$router
        .push({
          path: "/services",
          query: {
            serviceTypeId: subCategory.id, // 使用 serviceTypeId 对应服务列表的筛选参数
            parentCategory: this.selectedParentId,
            parentCategoryName: this.selectedParentCategory.title,
            subCategory: subCategory.id, // 保留原有参数以防其他地方使用
            subCategoryName: subCategory.name,
          },
        })
        .catch((err) => {
          console.error("跳转到服务列表失败:", err);
          this.$msg.error(ERROR_MESSAGES.NAVIGATION_FAILED);
        });
    },

    /**
     * 加载轮播图数据
     */
    async loadBanners() {
      try {
        const response = await noPermissionRequired.getBannerList(
          API_PARAMS.BANNERS
        );

        const bannerData = this.extractDataFromResponse(response);
        const validSlideshow = this.filterValidSlideshow(bannerData);
        this.banners = this.transformSlideshowData(validSlideshow);

        if (this.banners.length === 0) {
          console.warn("No valid banners found");
          // 添加默认轮播图
          this.banners = [
            {
              id: "default",
              title: DEFAULT_VALUES.BANNER_TITLE,
              image: IMAGE_CONFIG.DEFAULT_BANNER,
              linkUrl: "",
            },
          ];
        }
      } catch (error) {
        console.error(ERROR_MESSAGES.BANNER_FAILED, error);
        // 添加默认轮播图
        this.banners = [
          {
            id: "default",
            title: DEFAULT_VALUES.BANNER_TITLE,
            image: IMAGE_CONFIG.DEFAULT_BANNER,
            linkUrl: "",
          },
        ];
      }
    },

    /**
     * 过滤有效的轮播图数据
     */
    filterValidSlideshow(data) {
      return data.filter((item) => {
        const delFlag = item.delFlag || item.deleted || "0";
        const status = item.status || item.enabled || "0";
        return delFlag === "0" && status === "0";
      });
    },

    /**
     * 转换轮播图数据
     */
    transformSlideshowData(data) {
      return data.map((banner) => ({
        id: banner.id,
        title: banner.title || "",
        image: getImageUrl(banner.imageUrl),
        linkUrl: banner.linkUrl || "",
      }));
    },

    /**
     * 加载推荐商家数据
     */
    async loadRecommendMerchants() {
      try {
        const response = await noPermissionRequired.getMerchantRecommend(
          API_PARAMS.RECOMMEND_MERCHANTS
        );

        const merchantData = this.extractDataFromResponse(response);
        const validMerchants = this.filterValidMerchants(merchantData);
        const transformedMerchants = this.transformMerchantData(validMerchants);

        // 对推荐商家进行随机排序
        this.recommendMerchants = this.shuffleArray(transformedMerchants);

        if (this.recommendMerchants.length === 0) {
          console.warn("推荐商家数据为空");
        }
      } catch (error) {
        console.error(ERROR_MESSAGES.MERCHANT_FAILED, error);
        this.$msg.warning(ERROR_MESSAGES.MERCHANT_FAILED);
      }
    },

    /**
     * 过滤有效的商家数据
     */
    filterValidMerchants(data) {
      return this.filterValidData(data)
        .filter((item) => item.merchant)
        .slice(0, 6);
    },

    /**
     * 转换商家数据
     */
    transformMerchantData(data) {
      return data.map((item) => ({
        id: item.merchant.id,
        name: item.merchant.name || DEFAULT_VALUES.MERCHANT_NAME,
        rating: parseFloat(item.merchant.evaluate || DEFAULT_VALUES.RATING),
        image: this.getMerchantImage(item.merchant.merchantCover),
        area: item.merchant.area || DEFAULT_VALUES.AREA,
        service: this.getServiceCategoryName(item.merchant.service),
        remark: item.merchant.remark || "专业服务，值得信赖",
      }));
    },

    /**
     * 获取商家图片
     */
    getMerchantImage(merchantCover) {
      return merchantCover
        ? getImageUrl(merchantCover)
        : IMAGE_CONFIG.DEFAULT_MERCHANT;
    },

    /**
     * 根据服务类型ID获取分类名称
     */
    getServiceCategoryName(serviceId) {
      const category = this.parentCategories.find(
        (cat) => cat.id === serviceId
      );
      return category?.title || DEFAULT_VALUES.CATEGORY;
    },

    /**
     * 加载热门服务数据
     */
    async loadHotServices() {
      try {
        const response = await noPermissionRequired.getServiceListNoToken(
          API_PARAMS.HOT_SERVICES
        );

        const serviceData = this.extractDataFromResponse(response);
        const hotServices = this.filterHotServices(serviceData);
        const transformedServices = this.transformServiceData(hotServices);

        // 对热门服务进行随机排序
        this.hotServices = this.shuffleArray(transformedServices);

        if (this.hotServices.length === 0) {
          console.warn("热门服务数据为空");
        }
      } catch (error) {
        console.error(ERROR_MESSAGES.SERVICE_FAILED, error);
        this.$msg.warning(ERROR_MESSAGES.SERVICE_FAILED);
      }
    },

    /**
     * 过滤热门服务
     */
    filterHotServices(data) {
      return data
        .filter((item) => {
          const delFlag = item.delFlag || item.deleted || "0";
          const serviceStatus = item.serviceStatus || item.status || "1";
          const isRecommended = item.isRecommended || item.recommended || "0";

          return (
            delFlag === "0" &&
            serviceStatus === "1" &&
            isRecommended === "1" &&
            item.name &&
            item.merchantName
          );
        })
        .slice(0, 8);
    },

    /**
     * 转换服务数据
     */
    transformServiceData(data) {
      return data.map((service) => ({
        id: service.id,
        title: service.name,
        price: service.servicePrice,
        image: this.getServiceImage(service.avatarImg),
        merchantName: service.merchantName,
        salesNumber: service.salesNumber || 0,
        evaluate: service.evaluate || DEFAULT_VALUES.EVALUATE,
        address: this.formatAddress(service.address),
        categoryName: this.getServiceCategoryName(service.categoryId),
        description: this.formatServiceDescription(service.serviceDesc),
      }));
    },

    /**
     * 获取服务图片
     */
    getServiceImage(avatarImg) {
      return avatarImg ? getImageUrl(avatarImg) : IMAGE_CONFIG.DEFAULT_SERVICE;
    },

    /**
     * 格式化地址信息
     */
    formatAddress(address) {
      if (!address) return DEFAULT_VALUES.AREA;
      const match = address.match(/(成都市\w+区)/);
      return match ? match[1] : DEFAULT_VALUES.AREA;
    },

    /**
     * 格式化服务描述
     */
    formatServiceDescription(desc) {
      if (!desc) return DEFAULT_VALUES.SERVICE_DESC;
      return desc.length > 50 ? `${desc.substring(0, 50)}...` : desc;
    },

    /**
     * 加载平台信息
     */
    async loadPlatformInfo() {
      try {
        const response = await noPermissionRequired.getPlatformInfo(
          API_PARAMS.PLATFORM_INFO
        );

        const platformData = this.extractSingleDataFromResponse(response);
        if (platformData) {
          this.platformInfo = platformData;
          // 认证字段写入 Vuex store（地域筛选开关）
          if (platformData.certification !== undefined) {
            const raw = platformData.certification;
            // 业务含义：certification 字段用于控制“是否需要地域筛选”
            // 后端值格式形如 "1,0" 或 "1,1"，只取逗号后的那一段；若无逗号则取自身
            // 约定：取出的该段如果为 '0' => 需要地域筛选 (true)，为 '1' => 不需要地域筛选 (false)
            let extracted = raw;
            if (typeof raw === "string") {
              const parts = raw.split(",");
              extracted = parts.length > 1 ? parts[1] : parts[0];
            }
            const normalized = String(extracted).trim();
            const needRegionFilter = normalized === "0"; // 最终布尔：true=需要地域筛选
            // 提交到 store（字段名沿用 regionFilterEnabled，语义：开启地域筛选功能）
            this.$store.commit("setRegionFilterEnabled", true);
            // this.$store.commit("setRegionFilterEnabled", needRegionFilter);
          }
        }
      } catch (error) {
        console.warn(ERROR_MESSAGES.PLATFORM_FAILED, error);
        // 使用默认平台信息
        this.platformInfo = {
          id: "default",
          version: "V1.0.0",
          email: "support@example.com",
          phone: "客服电话",
        };
      }
    },

    /**
     * 处理商家点击 - 跳转到商家详情页
     */
    handleMerchantClick(merchant) {
      if (!merchant?.id) {
        this.$msg.warning(ERROR_MESSAGES.MERCHANT_INFO_INCOMPLETE);
        return;
      }

      this.$router
        .push({
          name: "merchantDetail",
          params: { id: merchant.id },
        })
        .catch((err) => {
          console.error("跳转到商家详情失败:", err);
          this.$msg.error("跳转失败，请稍后重试");
        });
    },

    /**
     * 处理服务点击 - 跳转到服务详情页
     */
    handleServiceClick(service) {
      if (!service?.id) {
        this.$msg.warning(ERROR_MESSAGES.SERVICE_INFO_INCOMPLETE);
        return;
      }

      this.$router
        .push({
          name: "serviceDetail",
          params: { id: service.id },
        })
        .catch((err) => {
          console.error("跳转到服务详情失败:", err);
          this.$msg.error("跳转失败，请稍后重试");
        });
    },

    /**
     * 导航到服务列表页
     */
    navigateToServices() {
      this.$router.push("/services").catch((err) => {
        console.error("跳转到服务列表失败:", err);
        this.$msg.error("跳转失败，请稍后重试");
      });
    },

    /**
     * 导航到商家列表页 - 根据登录状态选择不同页面
     */
    navigateToMerchants() {
      // 获取用户登录状态
      const isLoggedIn = this.$store.getters["user/isLoggedIn"];

      if (isLoggedIn) {
        // 已登录用户跳转到已登录商家列表页
        this.$router.push("/merchants/logged").catch((err) => {
          console.error("跳转到已登录商家列表失败:", err);
          this.$msg.error("跳转失败，请稍后重试");
        });
      } else {
        // 未登录用户跳转到未登录商家列表页
        this.$router.push("/merchants").catch((err) => {
          console.error("跳转到未登录商家列表失败:", err);
          this.$msg.error("跳转失败，请稍后重试");
        });
      }
    },

    /**
     * 处理图片加载错误
     */
    handleImageError(event) {
      console.warn("轮播图加载失败:", event.target.src);
      // 使用默认图片替换
      event.target.src = IMAGE_CONFIG.DEFAULT_BANNER;
    },

    /**
     * 处理图片加载成功
     */
    handleImageLoad(event) {
      // 图片加载成功，无需额外处理
    },

    /**
     * 处理图片加载并调整尺寸
     * @param {Event} event - 图片加载事件
     * @param {string} type - 图片类型 ('merchant' 或 'service')
     */
    handleImageLoadAndResize(event, type = "merchant") {
      const img = event.target;

      // 移除固定高度设置，让图片完全自适应
      // img.style.height = "auto"; // 让高度自适应
      img.style.objectFit = "cover";
    },

    /**
     * 处理商家图片加载
     */
    handleMerchantImageLoad(event) {
      this.handleImageLoadAndResize(event, "merchant");
    },

    /**
     * 处理服务图片加载
     */
    handleServiceImageLoad(event) {
      this.handleImageLoadAndResize(event, "service");
    },

    /**
     * 处理地域切换事件
     * @param {Object} payload 包含新城市信息 { province, city, code, name }
     */
    async handleRegionChange(payload) {
      try {
        // 仅在开启地域筛选时触发重新加载
        const enabled = this.$store.getters["user/regionFilterEnabled"];
        if (!enabled) return;

        this.$msg.info(
          `已切换至 ${
            payload.city || payload.name || "新城市"
          }，正在刷新数据...`
        );

        await this.reloadRegionRelatedData();
      } catch (e) {
        console.error("地域切换后刷新数据失败", e);
        this.$msg.error("地域数据刷新失败，请稍后重试");
      }
    },

    /**
     * 重新加载与地域相关的数据（推荐商家、热门服务）
     */
    async reloadRegionRelatedData() {
      // 可选：显示局部刷新状态，这里简单清空再加载
      this.recommendMerchants = [];
      this.hotServices = [];
      await Promise.all([
        this.loadRecommendMerchants(),
        this.loadHotServices(),
      ]);
    },
  },
};
</script>

<style lang="scss" scoped>
/* 首页容器样式 */
.home-container {
  min-height: 100vh;
  background: #f8f9fa;
}

/* 加载状态样式 */
.home-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;

  .el-icon-loading {
    font-size: 24px;
    color: #f5222d;
    animation: rotating 2s linear infinite;
  }

  p {
    margin-top: 16px;
    color: #7f8c8d;
    font-size: 16px;
  }
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* 轮播图区域样式 (重构) */
.home-banner-section {
  padding: 20px 0;
  position: relative;

  .home-banner-wrapper {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    border-radius: 12px;
    overflow: visible; /* 允许悬浮层超出容器 */
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    position: relative; /* 为悬浮层提供定位参照 */

    @media (max-width: 768px) {
      flex-direction: column;
      margin: 0 16px;
    }
  }

  .home-category-nav {
    flex: 0 0 240px; /* 减小宽度，让比例更协调 */
    background: #fff;
    border-right: 1px solid #e9ecef;

    @media (max-width: 768px) {
      flex: none;
      border-right: none;
      border-bottom: 1px solid #e9ecef;
    }

    .home-category-nav-header {
      background: #f5222d;
      color: #fff;
      padding: 14px 16px; /* 减小padding */
      text-align: center;

      h3 {
        margin: 0;
        font-size: 15px; /* 稍微减小字体 */
        font-weight: 600;
      }
    }

    .home-category-nav-list {
      max-height: 564px;
      overflow-y: auto;
      @media (max-width: 768px) {
        max-height: 200px;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1px;
        background: #f5f5f5;
      }
    }

    .home-category-nav-item {
      display: flex;
      align-items: center;
      padding: 10px 16px; /* 减小padding */
      cursor: pointer;
      transition: all 0.3s ease;
      border-bottom: 1px solid #f5f5f5;
      position: relative;
      background: #fff;

      @media (max-width: 768px) {
        padding: 8px 12px;
        border-bottom: none;

        .home-category-nav-name {
          font-size: 12px;
        }

        .el-icon-arrow-right {
          display: none;
        }
      }

      &:hover,
      &.active {
        background: #f8f9fa;
        color: #f5222d;

        .el-icon-arrow-right {
          transform: translateX(4px);
          color: #f5222d;
        }
      }

      .home-category-nav-icon {
        width: 28px; /* 减小图标尺寸 */
        height: 28px;
        margin-right: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        @media (max-width: 768px) {
          width: 24px;
          height: 24px;
          margin-right: 8px;
        }

        img {
          width: 20px; /* 减小图片尺寸 */
          height: 20px;
          object-fit: contain;

          @media (max-width: 768px) {
            width: 18px;
            height: 18px;
          }
        }
      }

      .home-category-nav-name {
        flex: 1;
        font-size: 13px; /* 减小字体 */
        font-weight: 500;
        color: #2c3e50;
        transition: color 0.3s ease;
      }

      .el-icon-arrow-right {
        font-size: 11px; /* 减小箭头 */
        color: #999;
        transition: all 0.3s ease;
      }
    }
  }
  .home-banner-content {
    flex: 1;
    position: relative;

    .el-carousel {
      height: 600px;

      @media (max-width: 768px) {
        height: 300px;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .home-banner-placeholder {
      height: 600px;
      background: #f5f5f5;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: 768px) {
        height: 300px;
      }

      .placeholder-content {
        text-align: center;
        color: #999;

        i {
          font-size: 48px;
          margin-bottom: 16px;
          display: block;

          @media (max-width: 768px) {
            font-size: 36px;
          }
        }

        p {
          font-size: 16px;
          margin: 0;

          @media (max-width: 768px) {
            font-size: 14px;
          }
        }
      }
    }
  }

  /* 二级分类悬浮层 (优化版) */
  .home-sub-category-float {
    position: absolute;
    /* left: 240px; 移除固定的left定位，改为动态计算 */
    width: 360px; /* 固定宽度，确保一行显示2-3个 */
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15); /* 增强阴影效果，确保在轮播图上也能清晰显示 */
    z-index: 1500; /* 提高层级，确保不被其他元素遮挡 */
    max-height: 320px;
    overflow-y: auto;
    backdrop-filter: blur(1px); /* 添加背景模糊效果，增强对比度 */

    @media (max-width: 768px) {
      position: fixed;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      width: auto;
      border-radius: 12px 12px 0 0;
      max-height: 60vh;
      z-index: 1500; /* 移动端也需要确保高层级 */
    }

    .home-sub-category-float-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      background: linear-gradient(135deg, #f5222d, #ff4d4f);
      color: #fff;
      border-radius: 8px 8px 0 0;

      h4 {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: #fff;
      }

      .home-sub-category-count {
        font-size: 11px;
        color: #fff;
        background: rgba(255, 255, 255, 0.2);
        padding: 2px 6px;
        border-radius: 8px;
      }
    }

    .home-sub-category-float-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr); /* 固定2列布局 */
      gap: 6px;
      padding: 12px;

      @media (max-width: 768px) {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        padding: 12px 16px;
      }
    }

    .home-sub-category-float-item {
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 4px;
      padding: 8px 12px;
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 13px;
      color: #2c3e50;
      line-height: 1.3;

      @media (max-width: 768px) {
        padding: 8px 12px;
        font-size: 12px;
      }

      &:hover {
        background: #f5222d;
        color: #fff;
        border-color: #f5222d;
        transform: translateY(-1px);
        box-shadow: 0 2px 6px rgba(245, 34, 45, 0.3);
      }
    }

    .home-sub-category-empty {
      padding: 24px 16px;
      text-align: center;
      color: #7f8c8d;
      font-size: 13px;

      @media (max-width: 768px) {
        padding: 20px 16px;
        font-size: 12px;
      }
    }
  }
}

/* 公共区域样式 */
.home-section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
}

.home-section-header {
  text-align: center;
  margin-bottom: 40px;

  h2 {
    font-size: 28px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 8px;
  }

  .home-section-desc {
    font-size: 16px;
    color: #7f8c8d;
  }

  .home-more-btn {
    color: #f5222d;
    font-size: 16px;

    &:hover {
      color: #cf1322;
    }
  }
}

/* 商家推荐区域样式 */
.home-merchant-section {
  background: #f8f9fa;

  .home-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;

    h2 {
      margin-bottom: 0;
    }
  }
}

.home-empty-block {
  margin: 100px auto 150px;
  max-width: 380px;
  padding: 12px 8px 14px;
  text-align: center;
  color: #666;
  box-shadow: none;

  i {
    font-size: 28px;
    color: #bbb;
    margin-bottom: 8px;
  }

  p {
    margin: 0 0 6px;
    font-size: 15px;
    font-weight: 500;
    color: #444;
  }

  small {
    display: block;
    font-size: 12px;
    color: #999;
    line-height: 1.4;
  }

  .el-button {
    margin-top: 8px;
    padding: 0 8px;
  }
}

.home-merchant-waterfall {
  column-count: 3;
  column-gap: 24px;
  margin-top: 40px;

  @media (max-width: 1200px) {
    column-count: 2;
  }

  @media (max-width: 768px) {
    column-count: 1;
  }
}

.home-merchant-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: inline-block;
  width: 100%;
  margin-bottom: 24px;
  break-inside: avoid;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  .home-merchant-image {
    width: 100%;
    overflow: hidden;
    position: relative;

    img {
      width: 100%;
      height: auto;
      object-fit: cover;
      transition: transform 0.3s ease;
      display: block;
    }

    &:hover img {
      transform: scale(1.05);
    }
  }

  .home-merchant-info {
    padding: 20px;

    .home-merchant-name {
      font-size: 18px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 8px;
      line-height: 1.4;
    }

    .home-merchant-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      font-size: 12px;
      color: #7f8c8d;
      flex-wrap: wrap;
      gap: 8px;

      .home-merchant-service {
        background: #e3f2fd;
        color: #1976d2;
        padding: 4px 8px;
        border-radius: 4px;
        white-space: nowrap;
      }

      .home-merchant-area {
        color: #666;
      }
    }

    .home-merchant-rating {
      margin-bottom: 12px;

      ::v-deep .el-rate {
        .el-rate__text {
          color: #7f8c8d;
          font-size: 14px;
        }
      }
    }

    .home-merchant-remark {
      font-size: 14px;
      color: #666;
      line-height: 1.5;
      margin: 0;
    }
  }
}

/* 热门服务区域样式 */
.home-service-section {
  padding-top: 30px;
  background: #fff;

  .home-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;

    h2 {
      margin-bottom: 0;
    }
  }
}

.home-service-waterfall {
  column-count: 4;
  column-gap: 24px;
  margin-top: 40px;

  @media (max-width: 1200px) {
    column-count: 3;
  }

  @media (max-width: 768px) {
    column-count: 2;
  }

  @media (max-width: 480px) {
    column-count: 1;
  }
}

.home-service-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: inline-block;
  width: 100%;
  margin-bottom: 24px;
  break-inside: avoid;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  .home-service-image {
    width: 100%;
    overflow: hidden;
    position: relative;

    img {
      width: 100%;
      height: auto;
      object-fit: cover;
      transition: transform 0.3s ease;
      display: block;
    }

    &:hover img {
      transform: scale(1.05);
    }

    .home-service-badge {
      position: absolute;
      top: 8px;
      right: 8px;
      background: rgba(255, 152, 0, 0.9);
      color: #fff;
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      backdrop-filter: blur(4px);
    }
  }

  .home-service-info {
    padding: 16px;

    .home-service-title {
      font-size: 16px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 8px;
      line-height: 1.4;
    }

    .home-service-description {
      font-size: 13px;
      color: #666;
      line-height: 1.4;
      margin-bottom: 12px;
    }

    .home-service-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      font-size: 12px;
      color: #7f8c8d;
      flex-wrap: wrap;
      gap: 8px;

      .home-service-merchant {
        font-weight: 500;
        color: #f5222d;
      }

      .home-service-area {
        color: #999;
      }
    }

    .home-service-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .home-service-price {
        font-size: 18px;
        font-weight: 600;
        color: #f5222d;
      }

      .home-service-rating {
        display: flex;
        align-items: center;
        font-size: 12px;
        color: #ff9900;

        i {
          margin-right: 2px;
        }
      }
    }
  }
}
</style>
